/**
 * CSV export of a Marketplace order list (SCRUM-2205) — Axiom, Partell, and any
 * vendor whose page renders through IntegrationOrdersSubTab.
 *
 * Built in the browser from the rows the table is showing, so the file is
 * exactly what the user filtered, sorted and chose to see (cancelled or not).
 * The list endpoint already returns everything a column needs; the server-side
 * list-export pipeline would have to re-implement these client-side filters.
 */
import { defuseFormula, toCsv } from '@/lib/csv';
import { triggerDownload } from '@/services/list-export.service';
import type { IntegrationOrder } from '@/services/integrations.service';

export interface OrderExportOptions {
  /** Fulfillment status code → the label the table shows. */
  statusLabels: Record<string, string>;
  /** Facility id → name; null/unknown ids export as ''. */
  facilityName: (facilityId: number | null | undefined) => string;
  /** Prescriber name from order metadata ('' when none). */
  prescriber: (order: IntegrationOrder) => string;
  /** Tracking number ('' when not shipped). */
  tracking: (order: IntegrationOrder) => string;
}

export const ORDER_EXPORT_HEADERS = [
  'Order #',
  'Ordered',
  'Patient',
  'Facility',
  'Items',
  'Prescriber',
  'Ordered by',
  'Status',
  'Payment',
  'Amount',
  'Currency',
  'Tracking',
] as const;

const withQty = (name: string, quantity: unknown) => {
  const qty = Number(quantity);
  return Number.isFinite(qty) && qty > 1 ? `${name} ×${qty}` : name;
};

/**
 * What the table's Product cell shows, in the same order of preference: the
 * vendor's own product name, else the order's rows ("BPC-157 ×2; Pen"), else
 * its catalog lines with their brand, else the catalog product name.
 */
export function itemsText(o: IntegrationOrder): string {
  const meta = o.metadata as Record<string, unknown> | null;
  const realName = (meta?.genovaProductName ?? meta?.skuName) as unknown;
  if (typeof realName === 'string' && realName.trim()) return realName.trim();
  const rows = (o.items ?? []).filter((i) => (i.name ?? '').trim() !== '');
  if (rows.length) return rows.map((i) => withQty(i.name.trim(), i.quantity)).join('; ');
  const lines = (meta?.lineItems as Array<{ name?: string; quantity?: number; brandName?: string }> | undefined) ?? [];
  const named = lines.filter((l) => (l.name ?? '').trim() !== '');
  if (named.length) {
    return named
      .map((l) => withQty(l.brandName ? `${l.brandName} — ${l.name!.trim()}` : l.name!.trim(), l.quantity))
      .join('; ');
  }
  return (o.productName ?? '').trim();
}

/** YYYY-MM-DD HH:mm in the viewer's time zone — sortable in a spreadsheet. */
export function dateText(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Header row + one row per order, in the order given. Pure, for tests. */
export function orderExportRows(orders: IntegrationOrder[], opts: OrderExportOptions): string[][] {
  const rows: string[][] = [[...ORDER_EXPORT_HEADERS]];
  for (const o of orders) {
    const fs = o.fulfillmentStatus ?? '';
    // Text from patients, staff and vendors is defused: a name starting with
    // = + - @ must not run as a formula when the file is opened in Excel.
    rows.push([
      String(o.integrationOrderId),
      dateText(o.createdAt),
      defuseFormula((o.patientName ?? '').trim()),
      defuseFormula(opts.facilityName(o.facilityId)),
      defuseFormula(itemsText(o)),
      defuseFormula(opts.prescriber(o)),
      defuseFormula((o.orderedBy ?? '').trim()),
      defuseFormula(fs ? (opts.statusLabels[fs] ?? fs) : ''),
      defuseFormula((o.status ?? '').trim()),
      Number.isFinite(o.amount) ? o.amount.toFixed(2) : '',
      o.currency || 'USD',
      defuseFormula(opts.tracking(o)),
    ]);
  }
  return rows;
}

/** "axiom-pens-orders-2026-10-12.csv" */
export function orderExportFileName(prefix: string, now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${prefix}-orders-${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}.csv`;
}

/**
 * Download rows as a CSV — every Marketplace export goes through here. A UTF-8
 * BOM keeps Excel from mangling names with accents or the × in quantities.
 */
export function downloadCsv(rows: string[][], fileName: string): void {
  const url = URL.createObjectURL(new Blob(['\uFEFF', toCsv(rows)], { type: 'text/csv;charset=utf-8' }));
  try {
    triggerDownload(url, fileName);
  } finally {
    // Revoked on the next tick: the click has already started the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export function downloadOrdersCsv(orders: IntegrationOrder[], opts: OrderExportOptions, fileName: string): void {
  downloadCsv(orderExportRows(orders, opts), fileName);
}
